import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Table2 } from 'lucide-react';
import { EmptyState } from './DashCard';
import { formatDuration } from '../../utils/duration';

const COLUMNS = [
  { key: 'template_name', label: 'Template', numeric: false },
  { key: 'median_seconds', label: 'Median time', numeric: true },
  { key: 'completion_rate_pct', label: 'Completion rate', numeric: true },
  { key: 'sent', label: 'Sent', numeric: true },
  { key: 'sample_size', label: 'Samples', numeric: true },
];

// Rows with no value for the sort key always sink to the bottom, either direction.
function sortRows(rows, key, dir) {
  const mult = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return a.template_name.localeCompare(b.template_name);
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
    return cmp * mult || a.template_name.localeCompare(b.template_name);
  });
}

export default function InsightsTable({ templates, minSample }) {
  const [sort, setSort] = useState({ key: 'median_seconds', dir: 'desc' }); // slowest first
  const rows = useMemo(() => sortRows(templates ?? [], sort.key, sort.dir), [templates, sort]);

  if (!rows.length) {
    return (
      <EmptyState
        icon={Table2}
        title="No templates to show yet"
        body="Once documents are sent and completed, each template's completion time and rate appear here."
        testId="insights-empty"
      />
    );
  }

  const toggle = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'template_name' ? 'asc' : 'desc' }));

  return (
    <div data-testid="completion-insights">
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-sm min-w-[440px]">
          <thead>
            <tr>
              {COLUMNS.map((c) => {
                const active = sort.key === c.key;
                const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`pb-2 font-normal whitespace-nowrap ${c.numeric ? 'text-right pl-2' : 'text-left'}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(c.key)}
                      className="dash-label inline-flex items-center gap-1 whitespace-nowrap"
                      style={active ? { color: 'var(--dash-accent-text)' } : undefined}
                      data-testid={`insights-sort-${c.key}`}
                    >
                      {c.label}
                      <Icon className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.template_id} style={{ borderTop: '1px solid var(--dash-border)' }} data-testid={`insights-row-${t.template_id}`}>
                <td className="py-2.5 pr-3 font-medium" style={{ color: 'var(--dash-text)' }}>
                  <div className="truncate max-w-[150px] xl:max-w-[280px]" title={t.template_name}>{t.template_name}</div>
                </td>
                <td className="py-2.5 pl-2 text-right tabular-nums whitespace-nowrap">
                  {t.median_seconds != null ? (
                    <span className="font-semibold" style={{ color: 'var(--dash-text)' }}>{formatDuration(t.median_seconds)}</span>
                  ) : (
                    <span className="dash-muted text-xs" title={`Needs at least ${minSample} completions in this period`}>Not enough data</span>
                  )}
                </td>
                <td className="py-2.5 pl-2 text-right tabular-nums" style={{ color: 'var(--dash-text)' }}>
                  {t.completion_rate_pct != null ? `${t.completion_rate_pct}%` : '—'}
                </td>
                <td className="py-2.5 pl-2 text-right tabular-nums dash-muted">{t.sent}</td>
                <td className="py-2.5 pl-2 text-right tabular-nums dash-muted">{t.sample_size}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="dash-caption mt-3">
        Median time is measured from the candidate first opening a document to submitting it, and only shown once a template has at least {minSample} completions in the period. Completion rate is completed ÷ sent for documents sent in the period.
      </p>
    </div>
  );
}
